import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { changeUserEmail } from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { userChangeEmail } from 'core/helpers/yupSchemas';
import { useGetUser } from 'core/hooks/useGetUser';

import { SendVerifyLetterEmailModal } from 'pages/Login/SendVerifyLetterModal';

import * as Styled from './styled';

export const ChangeEmail = () => {
	const { t: signUpPageTranslate } = useTranslation(NamespacesList['signUpPage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const [updatingLoading, setUpdatingLoading] = useState(false);
	const [openSendingVerify, setIsOpenSendingVerify] = useState(false);
	const [isDisabledChangeEmailButton, setIsDisabledChangeEmailButton] = useState(true);

	const { user } = useGetUser();
	const dispatch = useAppDispatch();

	const userData = useSelector(getUser);

	const {
		handleSubmit,
		register,
		formState: { errors },
		setValue,
		getValues,
		reset,
		watch,
	} = useForm({
		resolver: yupResolver(userChangeEmail()),
	});

	const currentEmailValue = watch('email');

	const handleActionSendingVerify = () => {
		setIsOpenSendingVerify((prev) => !prev);
	};

	const handleUpdate = async (data: any) => {
		setUpdatingLoading(true);
		try {
			await dispatch(
				changeUserEmail({ redirectLink: window.location.href, userEmail: data?.email })
			).unwrap();
			// Show success modal if email change is valid
			!openSendingVerify && handleActionSendingVerify();
		} catch (error: any) {
			// Extract error message from the rejected value
			// When rejectWithValue is called with a string, error is that string directly
			let errorMessage: string;
			
			if (typeof error === 'string') {
				// If error is directly a string (e.g., "User with email ... already exists")
				errorMessage = error;
			} else if (error?.message) {
				// If error has a message property
				errorMessage = error.message;
			} else if (error?.response?.data) {
				// If error has response.data
				errorMessage = typeof error.response.data === 'string' 
					? error.response.data 
					: error.response.data?.message || error.response.data;
			} else if (error?.data) {
				// If error has data property
				errorMessage = typeof error.data === 'string' 
					? error.data 
					: error.data?.message || JSON.stringify(error.data);
			} else {
				// Fallback to default error message
				errorMessage = commonTranslate('errors.somethingWentWrongDescription');
			}
			
			// Check if the error message indicates email already exists
			const errorMessageLower = errorMessage?.toLowerCase() || '';
			if (errorMessageLower.includes('already exists')) {
				// Display error snackbar for email already exists - DO NOT show success modal
				errorNotify(
					commonTranslate('errors.error'),
					errorMessage
				);
			} else {
				// Display generic error for other cases
				errorNotify(
					commonTranslate('errors.somethingWentWrongTitle'),
					commonTranslate('errors.somethingWentWrongDescription')
				);
			}
			console.error('Change email error:', error);
		} finally {
			setUpdatingLoading(false);
		}
	};

	useEffect(() => {
		if (user?.email) {
			reset();
			setValue('email', user.email);
		}
	}, [user]);

	useEffect(() => {
		setIsDisabledChangeEmailButton(
			!getValues().email || user?.email === getValues().email || updatingLoading
		);
	}, [currentEmailValue, updatingLoading, getValues, user?.email]);

	return (
		<form onSubmit={handleSubmit(handleUpdate)} style={{ width: '100%' }}>
			<Styled.ChangeEmailBox className="rtl-able">
				<FormControl fullWidth error={!!errors.email}>
					<Typography sx={{ fontSize: '12px' }}>{signUpPageTranslate('text.email')}</Typography>
					<TextField
						inputProps={{ style: { padding: '10px 12px' } }}
						placeholder="example@gmail.com"
						sx={{
							height: '44px',
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
						}}
						{...register('email')}
					/>
					{userData.signUpError ? (
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								position: 'absolute',
								right: '12px',
								top: '100%',
								color: '#FB0B24',
							}}
						>
							{signUpPageTranslate('text.userWithThisEmailAlreadyExists')}
						</FormHelperText>
					) : (
						<FormHelperText
							sx={{
								fontSize: '12px',
								textAlign: 'right',
								position: 'absolute',
								right: '12px',
								top: '100%',
							}}
						>
							{errors.email?.message}
						</FormHelperText>
					)}
				</FormControl>
				<Box>
					<Button type="submit" disabled={isDisabledChangeEmailButton}>
						{updatingLoading ? (
							<Oval
								height={24}
								width={24}
								color="rgba(0, 0, 0, 0.26)"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="rgba(0, 0, 0, 0.26)"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							commonTranslate('buttons.changeEmail')
						)}
					</Button>
				</Box>
				<SendVerifyLetterEmailModal
					onClose={handleActionSendingVerify}
					isOpen={openSendingVerify}
					onConfirm={() => handleUpdate(getValues())}
				/>
			</Styled.ChangeEmailBox>
		</form>
	);
};
